import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GridLogicOperator, type GridFilterModel, type GridSortModel } from '@mui/x-data-grid-pro';
import { useLocalState } from './useLocalState';

const SORT_PARAM = 'sort';
const FILTERS_PARAM = 'filters';
const REMEMBERED_SORT_KEY_PREFIX = 'physna-contents-sort';

function toSortModel(field: unknown, sort: unknown): GridSortModel {
  if (typeof field !== 'string' || !field) return [];
  if (sort !== 'asc' && sort !== 'desc') return [];
  return [{ field, sort }];
}

function parseSortParameter(value: string | null): GridSortModel {
  if (!value) return [];
  const [field, sort, ...rest] = value.split(':');
  return rest.length > 0 ? [] : toSortModel(field, sort);
}

function parseRememberedSortModel(value: unknown): GridSortModel {
  if (!Array.isArray(value) || value.length === 0) return [];
  const [first] = value;
  return toSortModel(first?.field, first?.sort);
}

function emptyFilterModel(): GridFilterModel {
  return { items: [] };
}

function parseFiltersParameter(value: string | null): GridFilterModel {
  if (!value) return emptyFilterModel();
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return { items: parsed };
    if (parsed && Array.isArray(parsed.items)) {
      const model: GridFilterModel = { items: parsed.items };
      if (parsed.logicOperator === 'or') model.logicOperator = GridLogicOperator.Or;
      return model;
    }
    return emptyFilterModel();
  } catch {
    return emptyFilterModel();
  }
}

export function useGridStateParameters(tenantId: string) {
  const [searchParameters, setSearchParameters] = useSearchParams();

  // A sort in the URL wins only until another tab changes the sort; then this tab follows too.
  const dropUrlSort = useCallback(() => {
    setSearchParameters(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete(SORT_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParameters]);

  const [rememberedSortModel, setRememberedSortModel] = useLocalState<unknown>(
    `${REMEMBERED_SORT_KEY_PREFIX}:${tenantId}`,
    [],
    dropUrlSort
  );

  const sortParameter = searchParameters.get(SORT_PARAM);
  const urlSortModel = useMemo(() => parseSortParameter(sortParameter), [sortParameter]);
  const isSortRemembered = urlSortModel.length === 0;
  const sortModel = useMemo(
    () => (isSortRemembered ? parseRememberedSortModel(rememberedSortModel) : urlSortModel),
    [isSortRemembered, urlSortModel, rememberedSortModel]
  );

  const filterModel = useMemo(() => parseFiltersParameter(searchParameters.get(FILTERS_PARAM)), [searchParameters]);

  const onSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      const activeSort = newModel.length > 0 && newModel[0].sort ? newModel[0] : undefined;
      setRememberedSortModel(activeSort ? [activeSort] : []);
      setSearchParameters(previous => {
        const next = new URLSearchParams(previous);
        if (activeSort) {
          next.set(SORT_PARAM, `${activeSort.field}:${activeSort.sort}`);
        } else {
          next.delete(SORT_PARAM);
        }
        return next;
      });
    },
    [setSearchParameters, setRememberedSortModel]
  );

  const onFilterModelChange = useCallback(
    (newModel: GridFilterModel) => {
      setSearchParameters(previous => {
        const next = new URLSearchParams(previous);

        if (newModel.items.length > 0) {
          const serialized: { items: typeof newModel.items; logicOperator?: string } = { items: newModel.items };
          if (newModel.logicOperator === GridLogicOperator.Or) serialized.logicOperator = 'or';
          next.set(FILTERS_PARAM, JSON.stringify(serialized));
        } else {
          next.delete(FILTERS_PARAM);
        }
        return next;
      });
    },
    [setSearchParameters]
  );

  const onNegationChange = useCallback(
    (itemId: string | number, negated: boolean, currentFilterModel: GridFilterModel) => {
      const updatedItems = currentFilterModel.items.map(item =>
        item.id === itemId ? { ...item, negated: negated || undefined } : item
      );
      setSearchParameters(previous => {
        const next = new URLSearchParams(previous);
        const serialized: { items: typeof updatedItems; logicOperator?: string } = { items: updatedItems };
        if (currentFilterModel.logicOperator === GridLogicOperator.Or) serialized.logicOperator = 'or';
        next.set(FILTERS_PARAM, JSON.stringify(serialized));
        return next;
      });
    },
    [setSearchParameters]
  );

  return { sortModel, isSortRemembered, filterModel, onSortModelChange, onFilterModelChange, onNegationChange };
}
